import { cbSemanticCssVars } from '@creditornot/cb-ingredients';
import { Spinner } from '@creditornot/cb-progress';
import { useQuery } from '@tanstack/react-query';
import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import styled from 'styled-components';

import { getMenuConfigs } from 'apis/restaurant-api/merchant-admin/menu2/api';
import { useLocalStorageJSON, useVenuePosIntegrationStatus, useExternalAccessRights } from 'common/hooks';
import { usePermissions } from 'common/wolt-permissions';
import * as permissionUtils from 'common/wolt-permissions/permissionUtils';
import { languages } from 'config';
import PageLoadError from 'core/PageLoadError';
import { useCountryConfigContext } from 'modules/country/CountryConfigContext';
import useRestockingRulesFeatureFlag from 'modules/menu/restocking-rules/useRestockingRulesFeatureFlag';

import MenuEditor from './MenuEditor';
import PosWarningModal from './PosWarningModal';
import { VatCategoriesParamsContextProvider } from './VatCategories';
import { useMenuModuleContext } from '../context';
import CreateMenuForm from '../create-menu/CreateMenuForm';
import { useSharedMenuLink } from '../hooks/useSharedMenuLink';
import useMenuData from '../useMenuData';
import { buildMenuPermissions, buildMenuFeatures } from '../utils';

const SpinnerContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${cbSemanticCssVars.surfaceMain};
`;

const isLanguage = (language: any): language is Language => languages.includes(language);

export const ClassicMenuEditorPage = () => {
  const permissions = usePermissions();
  const externalAccessRights = useExternalAccessRights();
  const countryConfig = useCountryConfigContext();
  const { venue } = useMenuModuleContext();
  const { isSharedMenuLinkLoading } = useSharedMenuLink();

  useEffect(() => {
    // Dismiss all menu related toasts, if any, when leaving the menu editor
    return () => toast.dismiss();
  }, []);

  const [isPosWarningModalOpen, setShowPosWarning] = useState(false);
  const {
    data: venuePosIntegrationStatus,
    error: venuePosIntegrationStatusError,
    isFetching: fetchingVenuePosIntegrationStatus,
  } = useVenuePosIntegrationStatus(venue.id);

  const [visitedPosIntegratedVenues, setVisitedPosIntegratedVenues] = useLocalStorageJSON<string[]>(
    'pos_integration.venues',
    [],
  );

  const { menu, loadingMenuData, menuDataError, refetchMenuData } = useMenuData();

  const { data: menuConfigs } = useQuery(
    ['menu-configs', venue.menu],
    () => (venue.menu ? getMenuConfigs(venue.menu) : null),
    {
      enabled: !!venue.menu,
      staleTime: Infinity,
    },
  );

  const { enabled: restockingRulesEnabled } = useRestockingRulesFeatureFlag(venue.country);

  const menuPermissions = useMemo(() => {
    return buildMenuPermissions(permissions, externalAccessRights, venue, venuePosIntegrationStatus);
  }, [permissions, externalAccessRights, venue, venuePosIntegrationStatus]);

  useEffect(() => {
    if (
      !fetchingVenuePosIntegrationStatus &&
      venuePosIntegrationStatus?.is_menu_integrated &&
      !menuPermissions.contentEdit &&
      !visitedPosIntegratedVenues.includes(venue.id)
    ) {
      setShowPosWarning(true);
    }
  }, [venuePosIntegrationStatus, menuPermissions, venue]); // eslint-disable-line react-hooks/exhaustive-deps

  const setVisitedVenue = (venueId: string) => {
    if (!visitedPosIntegratedVenues.includes(venueId)) {
      setVisitedPosIntegratedVenues([...visitedPosIntegratedVenues, venueId]);
    }
  };

  const hidePosWarning = () => {
    setVisitedVenue(venue.id);
    setShowPosWarning(false);
  };

  const menuFeatures = useMemo(
    () =>
      buildMenuFeatures({
        venue,
        venueIsPosIntegrated: venuePosIntegrationStatus?.is_menu_integrated || false,
        hasCatalogSource: !!menuConfigs?.source_config,
        countryConfig,
        restockingRulesEnabled,
      }),
    [venue, venuePosIntegrationStatus?.is_menu_integrated, countryConfig, restockingRulesEnabled, menuConfigs],
  );

  const vatCategoriesParams = useMemo(() => ({ country: venue.country, venueCity: venue.city }), [venue]);

  if (!countryConfig) {
    throw new Error('No country config loaded');
  }

  if (menuDataError || venuePosIntegrationStatusError || !venue) {
    return <PageLoadError />;
  }

  if (!venue.menu) {
    return (
      <CreateMenuForm
        hasCreateMenuPermission={permissionUtils.hasCreateMenuPermission(permissions, externalAccessRights, venue)}
        defaultLanguage={isLanguage(countryConfig.primary_language) ? countryConfig.primary_language : undefined}
        venueId={venue.id}
        venueSlug={venue.slug}
      />
    );
  }

  if (!menu || loadingMenuData || fetchingVenuePosIntegrationStatus || isSharedMenuLinkLoading) {
    return (
      <SpinnerContainer>
        <Spinner size="large" color="blue" />
      </SpinnerContainer>
    );
  }

  return (
    <VatCategoriesParamsContextProvider value={vatCategoriesParams}>
      <MenuEditor
        country={venue.country}
        menuFeatures={menuFeatures}
        menuId={menu.id}
        menuPermissions={menuPermissions}
        merchantId={venue.merchant}
        primaryLanguage={menu.primary_language}
        refetchMenu={refetchMenuData}
        timezone={venue.timezone}
        venueId={venue.id}
        venueSlug={venue.slug}
        woltcomUrl={venue.public_url}
      />
      <PosWarningModal close={hidePosWarning} isOpen={isPosWarningModalOpen} />
    </VatCategoriesParamsContextProvider>
  );
};
